import React, { useEffect, useRef, useState } from "react"

// Carte éditoriale du quartier façon Apple Plans, dessinée en SVG maison
// (aucune tuile externe) : l'adresse au centre, des cercles concentriques de
// temps de marche, et des repères placés par projection de leurs vraies
// coordonnées (équirectangulaire locale, exacte à l'échelle d'un quartier).
// Temps de marche = distance à vol d'oiseau (haversine) × détour / vitesse.
// Survol, focus ou tap sur un repère → étiquette ; la liste à côté (rendue
// dans le HTML, SEO) pilote la même sélection. Mobile : carte puis liste.
//
// center : { name, lat, lon }
// points : [{ name, lat, lon, kind, note }]
// lines  : [{ name?, kind:"river"|"avenue", coords:[[lat,lon],…], labelAt?:index, widthM?, minor? }]
//          (minor : libellé masqué sur mobile pour ne pas surcharger la carte)
// ringAngles : angle (degrés, sens horaire depuis l'est) de l'étiquette de chaque cercle
// labels : { aria, minutes:"≈ {n} min", walk, hint, listTitle, note, kinds:{ kind: libellé } }
//
// Évitement de collision : mesurée en pixels réels (ResizeObserver), une
// relaxation écarte les pastilles trop proches entre elles, du point central
// et de son étiquette (décalage radial depuis l'adresse). Une pastille
// déplacée garde un petit trait de rappel vers son point réel.

const KIND_COLORS = {
  hotel: "#b07d2b",
  shopping: "#d6336c",
  culture: "#7d4cdb",
  landmark: "#e8590c",
  transport: "#0071e3",
  river: "#1c7ed6",
}

const STYLE = `
  .nm-grid { display:grid; grid-template-columns:minmax(0,1.45fr) minmax(0,1fr); gap:32px; align-items:start; }
  .nm-map { position:relative; width:100%; aspect-ratio:1/1; border-radius:28px; overflow:hidden; background:#f3f1ec; border:1px solid rgba(0,0,0,0.06); box-shadow:0 1px 2px rgba(0,0,0,0.04); }
  .nm-map svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
  .nm-ring-lbl { position:absolute; z-index:1; transform:translate(-50%,-50%); font-size:11px; font-weight:600; letter-spacing:0.02em; color:#0062c4; background:rgba(255,255,255,0.92); border-radius:999px; padding:2px 8px; pointer-events:none; white-space:nowrap; }
  .nm-road-lbl { position:absolute; transform-origin:center; font-size:10.5px; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; color:rgba(60,60,67,0.55); white-space:nowrap; pointer-events:none; }
  .nm-river-lbl { color:#3b7fc0; font-style:italic; text-transform:none; letter-spacing:0.02em; font-size:12px; }
  .nm-pt { position:absolute; width:40px; height:40px; margin:-20px 0 0 -20px; padding:0; border:0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; z-index:2; -webkit-tap-highlight-color:transparent; }
  .nm-leaders { position:absolute; inset:0; width:100%; height:100%; z-index:2; pointer-events:none; overflow:visible; }
  .nm-dot { width:22px; height:22px; border-radius:50%; border:2px solid #fff; color:#fff; font-size:12px; font-weight:700; line-height:18px; text-align:center; box-shadow:0 1px 4px rgba(0,0,0,0.28); transition:transform 260ms cubic-bezier(0.22,1,0.36,1); font-variant-numeric:tabular-nums; }
  .nm-pt:hover .nm-dot, .nm-pt:focus-visible .nm-dot, .nm-pt.is-on .nm-dot { transform:scale(1.35); }
  .nm-pt.is-on { z-index:4; }
  .nm-pt:focus-visible { outline:none; }
  .nm-center { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); z-index:1; pointer-events:none; }
  .nm-center-dot { width:22px; height:22px; border-radius:50%; background:#1d1d1f; border:3px solid #fff; box-shadow:0 0 0 6px rgba(29,29,31,0.12), 0 2px 8px rgba(0,0,0,0.3); }
  .nm-center-lbl { position:absolute; right:calc(100% + 8px); top:50%; transform:translateY(-50%); background:#1d1d1f; color:#fff; font-size:12.5px; font-weight:600; letter-spacing:-0.01em; padding:5px 11px; border-radius:999px; white-space:nowrap; box-shadow:0 4px 14px rgba(0,0,0,0.18); }
  .nm-tip { position:absolute; z-index:5; min-width:150px; max-width:230px; background:rgba(255,255,255,0.97); backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px); border-radius:14px; padding:10px 13px 11px; box-shadow:0 10px 30px rgba(0,0,0,0.18); pointer-events:none; animation:nmTip 260ms cubic-bezier(0.22,1,0.36,1); }
  .nm-tip-name { font-size:14px; font-weight:600; letter-spacing:-0.02em; color:#1d1d1f; line-height:1.25; }
  .nm-tip-time { font-size:13px; font-weight:600; color:#0071e3; margin-top:3px; }
  .nm-tip-note { font-size:12px; color:#6e6e73; line-height:1.4; margin-top:3px; }
  @keyframes nmTip { from { opacity:0; transform:var(--nm-t) translateY(6px); } to { opacity:1; transform:var(--nm-t); } }
  .nm-hint { position:absolute; left:14px; top:12px; font-size:12px; color:rgba(60,60,67,0.7); background:rgba(255,255,255,0.85); border-radius:999px; padding:4px 10px; pointer-events:none; }
  .nm-list-title { font-size:12px; letter-spacing:0.16em; text-transform:uppercase; color:#6e6e73; margin:0 0 10px; }
  .nm-list { list-style:none; margin:0; padding:0; }
  .nm-item button { display:flex; align-items:center; gap:12px; width:100%; padding:10px 10px; margin:0; border:0; border-radius:12px; background:transparent; font:inherit; text-align:left; cursor:pointer; color:#1d1d1f; transition:background 200ms; }
  .nm-item button:hover, .nm-item button:focus-visible, .nm-item.is-on button { background:rgba(0,113,227,0.07); outline:none; }
  .nm-item .nm-dot { flex:0 0 auto; }
  .nm-item-main { flex:1; min-width:0; }
  .nm-item-name { display:block; font-size:15px; font-weight:600; letter-spacing:-0.015em; line-height:1.3; }
  .nm-item-note { display:block; font-size:13px; color:#6e6e73; line-height:1.4; margin-top:1px; }
  .nm-item-time { flex:0 0 auto; font-size:14px; font-weight:600; color:#1d1d1f; white-space:nowrap; font-variant-numeric:tabular-nums; }
  .nm-note { font-size:13px; color:#6e6e73; font-style:italic; margin:14px 0 0; }
  @media (max-width: 860px) {
    .nm-grid { grid-template-columns:1fr; gap:20px; }
    .nm-map { border-radius:22px; }
    .nm-road-lbl { font-size:11px; letter-spacing:0.06em; }
    .nm-river-lbl { font-size:12px; letter-spacing:0.02em; }
    .nm-road-minor { display:none; }
    .nm-ring-lbl { font-size:12px; padding:1px 7px; }
    .nm-center-lbl { font-size:12px; padding:4px 10px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .nm-tip { animation:none; }
    .nm-dot { transition:none; }
  }
`

const toRad = d => (d * Math.PI) / 180

const haversine = (a, b) => {
  const R = 6371000
  const dp = toRad(b.lat - a.lat)
  const dl = toRad(b.lon - a.lon)
  const h = Math.sin(dp / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dl / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

// tracé adouci (courbes quadratiques passant par les milieux) pour la Seine
const smooth = pts => {
  if (pts.length < 3) return `M${pts.map(p => `${p.x},${p.y}`).join(" L")}`
  let d = `M${pts[0].x},${pts[0].y}`
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2
    const my = (pts[i].y + pts[i + 1].y) / 2
    d += ` Q${pts[i].x},${pts[i].y} ${mx},${my}`
  }
  const last = pts[pts.length - 1]
  return `${d} L${last.x},${last.y}`
}

// Relaxation en pixels : pastilles (diamètre DOT) ≥ GAP l'une de l'autre,
// hors du disque central et de l'étiquette de l'adresse.
const DOT = 22
const GAP = DOT + 6
const CENTER_CLEAR = 17 + DOT / 2 + 4
const layoutPins = (pts, w, rects) => {
  const c = w / 2
  const pos = pts.map(p => ({ x: p.x, y: p.y }))
  const lim = DOT / 2 + 2
  for (let it = 0; it < 120; it++) {
    let moved = false
    pos.forEach((q, i) => {
      // disque central : poussée radiale depuis l'adresse
      let dx = q.x - c
      let dy = q.y - c
      let d = Math.hypot(dx, dy)
      if (d < CENTER_CLEAR) {
        if (d < 0.5) { const a = (i * 2.4) % (2 * Math.PI); dx = Math.cos(a); dy = Math.sin(a); d = 1 }
        q.x = c + (dx / d) * CENTER_CLEAR
        q.y = c + (dy / d) * CENTER_CLEAR
        moved = true
      }
      // étiquettes (adresse, temps des cercles) : la pastille en sort par le
      // bord le plus proche
      rects.forEach(lbl => {
        const m = DOT / 2 + 3
        const l = lbl.left - m, r = lbl.right + m, t = lbl.top - m, b = lbl.bottom + m
        if (q.x > l && q.x < r && q.y > t && q.y < b) {
          const opts = [[0, t - q.y], [0, b - q.y], [l - q.x, 0], [r - q.x, 0]]
          const [mx, my] = opts.reduce((best, o) => (Math.abs(o[0]) + Math.abs(o[1]) < Math.abs(best[0]) + Math.abs(best[1]) ? o : best))
          q.x += mx
          q.y += my
          moved = true
        }
      })
    })
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const a = pos[i], b = pos[j]
        let dx = b.x - a.x
        let dy = b.y - a.y
        let d = Math.hypot(dx, dy)
        if (d >= GAP) continue
        if (d < 0.5) { dx = 1; dy = 0; d = 1 }
        // le plus lointain de l'adresse s'écarte davantage (décalage radial)
        const da = Math.hypot(a.x - c, a.y - c), db = Math.hypot(b.x - c, b.y - c)
        const k = (GAP - d) / d
        const wa = da > db ? 0.3 : 0.7
        a.x -= dx * k * wa
        a.y -= dy * k * wa
        b.x += dx * k * (1 - wa)
        b.y += dy * k * (1 - wa)
        moved = true
      }
    }
    pos.forEach(q => { q.x = Math.max(lim, Math.min(w - lim, q.x)); q.y = Math.max(lim, Math.min(w - lim, q.y)) })
    if (!moved) break
  }
  return pos
}

const NeighbourhoodMap = ({ center, points, labels = {}, lines = [], rings = [5, 10, 15], ringAngles = [135, -35, -60], speedKmh = 4.8, detour = 1.3, radiusM }) => {
  const [active, setActive] = useState(null)
  const [pinned, setPinned] = useState(null)
  const mapRef = useRef(null)
  const lblRef = useRef(null)
  const [box, setBox] = useState(null)

  // taille réelle de la carte (carrée) et de l'étiquette centrale, en px
  useEffect(() => {
    const el = mapRef.current
    if (!el || typeof ResizeObserver === "undefined") return undefined
    const read = () => {
      const w = el.clientWidth
      const lb = lblRef.current
      const rects = []
      if (lb) rects.push({ left: w / 2 - 19 - lb.offsetWidth, right: w / 2 - 19, top: w / 2 - lb.offsetHeight / 2, bottom: w / 2 + lb.offsetHeight / 2 })
      // étiquettes des cercles (centrées sur leur point d'ancrage)
      el.querySelectorAll(".nm-ring-lbl").forEach(r => {
        const cx = (parseFloat(r.style.left) / 100) * w, cy = (parseFloat(r.style.top) / 100) * w
        rects.push({ left: cx - r.offsetWidth / 2, right: cx + r.offsetWidth / 2, top: cy - r.offsetHeight / 2, bottom: cy + r.offsetHeight / 2 })
      })
      const sig = `${w}|${rects.map(r => Math.round(r.right - r.left)).join(",")}`
      setBox(prev => (prev && prev.sig === sig ? prev : { w, sig, rects }))
    }
    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const mPerMin = (speedKmh * 1000) / 60 / detour
  const kx = Math.cos(toRad(center.lat)) * 111320
  const ky = 110574
  const project = (lat, lon) => ({ x: (lon - center.lon) * kx, y: -(lat - center.lat) * ky })

  const enriched = points
    .map(p => {
      const dist = haversine(center, p)
      const { x, y } = project(p.lat, p.lon)
      return { ...p, dist, x, y, minutes: Math.max(1, Math.round(dist / mPerMin)) }
    })
    .sort((a, b) => a.dist - b.dist)
    .map((p, i) => ({ ...p, n: i + 1 }))

  const R = radiusM || Math.ceil((Math.max(...enriched.map(p => p.dist), rings[rings.length - 1] * mPerMin) * 1.1) / 50) * 50
  const pct = v => 50 + (v / R) * 50
  const fmtMin = n => (labels.minutes || "≈ {n} min").replace("{n}", n)
  const color = kind => KIND_COLORS[kind] || "#1d1d1f"

  // positions affichées (px → %) : réelles au rendu serveur, puis écartées
  const placed = box && box.w > 0
    ? layoutPins(enriched.map(p => ({ x: (pct(p.x) / 100) * box.w, y: (pct(p.y) / 100) * box.w })), box.w, box.rects).map(q => ({ left: (q.x / box.w) * 100, top: (q.y / box.w) * 100 }))
    : enriched.map(p => ({ left: pct(p.x), top: pct(p.y) }))

  const shown = active !== null ? active : pinned
  const cur = shown !== null ? enriched[shown] : null
  const tipPos = i => {
    const { left, top } = placed[i]
    const tx = left < 22 ? "-12%" : left > 78 ? "-88%" : "-50%"
    const below = top < 24
    const t = `translate(${tx}, ${below ? "18px" : "calc(-100% - 18px)"})`
    return { left: `${left}%`, top: `${top}%`, transform: t, "--nm-t": t }
  }

  const roadLabel = line => {
    const pts = line.coords.map(([lat, lon]) => project(lat, lon))
    const i = Math.min(pts.length - 2, Math.max(0, line.labelAt != null ? line.labelAt : Math.floor((pts.length - 1) / 2)))
    const a = pts[i]
    const b = pts[i + 1]
    let ang = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI
    if (ang > 90) ang -= 180
    if (ang < -90) ang += 180
    const mx = (a.x + b.x) / 2
    const my = (a.y + b.y) / 2
    return { left: `${pct(mx)}%`, top: `${pct(my)}%`, transform: `translate(-50%,-50%) rotate(${ang}deg)` }
  }

  const onPoint = i => {
    if (pinned === i) { setPinned(null); setActive(null) } else setPinned(i)
  }

  return (
    <div className="nm-wrap">
      <style>{STYLE}</style>
      <div className="nm-grid">
        <div>
          <div ref={mapRef} className="nm-map" role="group" aria-label={labels.aria || center.name} onMouseLeave={() => setActive(null)}>
            <svg viewBox={`${-R} ${-R} ${2 * R} ${2 * R}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
              {rings.slice().reverse().map(m => (
                <circle key={`f${m}`} cx="0" cy="0" r={m * mPerMin} fill="rgba(0,113,227,0.045)" />
              ))}
              {lines.filter(l => l.kind === "river").map((l, i) => (
                <path key={`r${i}`} d={smooth(l.coords.map(([lat, lon]) => project(lat, lon)))} fill="none" stroke="#b6d6f2" strokeWidth={l.widthM || 120} strokeLinecap="round" strokeLinejoin="round" />
              ))}
              {lines.filter(l => l.kind !== "river").map((l, i) => {
                const pts = l.coords.map(([lat, lon]) => { const p = project(lat, lon); return `${p.x},${p.y}` }).join(" ")
                return (
                  <g key={`a${i}`}>
                    <polyline points={pts} fill="none" stroke="#e2ddd2" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    <polyline points={pts} fill="none" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                  </g>
                )
              })}
              {rings.map(m => (
                <circle key={`s${m}`} cx="0" cy="0" r={m * mPerMin} fill="none" stroke="#0071e3" strokeOpacity="0.45" strokeWidth="1.25" strokeDasharray="4 5" vectorEffect="non-scaling-stroke" />
              ))}
            </svg>

            {lines.filter(l => l.name).map((l, i) => (
              <span key={`l${i}`} className={`nm-road-lbl${l.kind === "river" ? " nm-river-lbl" : ""}${l.minor ? " nm-road-minor" : ""}`} style={roadLabel(l)}>{l.name}</span>
            ))}
            {rings.map((m, k) => {
              const a = toRad(ringAngles[k % ringAngles.length])
              return <span key={`rl${m}`} className="nm-ring-lbl" style={{ left: `${pct(Math.cos(a) * m * mPerMin)}%`, top: `${pct(Math.sin(a) * m * mPerMin)}%` }}>{fmtMin(m)}</span>
            })}

            <div className="nm-center">
              <div className="nm-center-dot" />
              <span ref={lblRef} className="nm-center-lbl">{center.name}</span>
            </div>

            {/* traits de rappel : pastille écartée → point réel */}
            <svg className="nm-leaders" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
              {enriched.map((p, i) => {
                const x0 = pct(p.x), y0 = pct(p.y), q = placed[i]
                if (!box || Math.hypot((q.left - x0) * box.w, (q.top - y0) * box.w) / 100 < 5) return null
                return (
                  <g key={p.name}>
                    <line x1={x0} y1={y0} x2={q.left} y2={q.top} stroke={color(p.kind)} strokeWidth="1.5" strokeOpacity="0.85" vectorEffect="non-scaling-stroke" />
                    <circle cx={x0} cy={y0} r={3.5 * 100 / box.w} fill={color(p.kind)} stroke="#fff" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                  </g>
                )
              })}
            </svg>

            {enriched.map((p, i) => (
              <button
                key={p.name}
                type="button"
                className={`nm-pt${shown === i ? " is-on" : ""}`}
                style={{ left: `${placed[i].left}%`, top: `${placed[i].top}%` }}
                aria-label={`${p.name}, ${fmtMin(p.minutes)} ${labels.walk || ""}`.trim()}
                aria-pressed={shown === i}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                onClick={() => onPoint(i)}
              >
                <span className="nm-dot" style={{ background: color(p.kind) }}>{p.n}</span>
              </button>
            ))}

            {cur ? (
              <div key={cur.name} className="nm-tip" style={tipPos(shown)} role="status">
                <div className="nm-tip-name">{cur.name}</div>
                <div className="nm-tip-time">{fmtMin(cur.minutes)} {labels.walk}</div>
                {cur.note ? <div className="nm-tip-note">{cur.note}</div> : null}
              </div>
            ) : labels.hint ? (
              <span className="nm-hint">{labels.hint}</span>
            ) : null}
          </div>
          {labels.note ? <p className="nm-note">{labels.note}</p> : null}
        </div>

        <div>
          {labels.listTitle ? <p className="nm-list-title">{labels.listTitle}</p> : null}
          <ol className="nm-list">
            {enriched.map((p, i) => (
              <li key={p.name} className={`nm-item${shown === i ? " is-on" : ""}`}>
                <button
                  type="button"
                  aria-pressed={shown === i}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  onClick={() => onPoint(i)}
                >
                  <span className="nm-dot" style={{ background: color(p.kind) }} aria-hidden="true">{p.n}</span>
                  <span className="nm-item-main">
                    <span className="nm-item-name">{p.name}</span>
                    {p.note ? <span className="nm-item-note">{p.note}</span> : null}
                  </span>
                  <span className="nm-item-time">{fmtMin(p.minutes)}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  )
}

export default NeighbourhoodMap
